<template>
  <div class="price-table-wrapper">
    <!-- Header Section -->
    <div class="price-list-header">
      <h2 class="price-list-title">Service Price List {{ priceData.year }}</h2>
      <p class="price-list-currency">(In {{ priceData.currency }}, Excl. {{ priceData.vatRate }}% VAT)</p>
      <p class="price-list-note">{{ priceData.note }}</p>
    </div>

    <!-- Tables Section -->
    <div class="price-table-container">
      <div class="price-tables-content">
        <!-- Category Groups -->
        <div v-for="(category, categoryIndex) in priceData.categories" :key="categoryIndex" class="table-category">
          <h3 class="category-title">{{ category.title }}</h3>

          <!-- Tables in Category -->
          <div :class="getTableLayoutClass(category)">
            <div v-for="(table, tableIndex) in category.tables" :key="tableIndex"
                 :class="getTableClass(category, table)">
              <table class="price-table">
                <thead>
                  <tr v-if="table.name" class="table-name-row">
                    <th :colspan="table.headers.length">{{ table.name }}</th>
                  </tr>
                  <tr class="table-header-row">
                    <th v-for="(header, headerIndex) in table.headers" :key="headerIndex">
                      {{ header }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Regular Rows -->
                  <tr v-for="(row, rowIndex) in table.rows" :key="rowIndex">
                    <td v-for="(cell, cellIndex) in row" :key="cellIndex"
                        :class="getCellClass(cellIndex, row.length)">
                      {{ cell }}
                    </td>
                  </tr>
                  <!-- Highlighted Rows -->
                  <tr v-for="(row, rowIndex) in (table.highlightedRows || [])"
                      :key="`highlight-${rowIndex}`"
                      class="highlighted-row">
                    <td v-for="(cell, cellIndex) in row" :key="cellIndex"
                        :class="getCellClass(cellIndex, row.length)">
                      {{ cell }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Delivery Fee Note -->
        <div class="delivery-note">
          <p>
            <strong>Packing & Admin Fee for Delivery:</strong>
            €{{ priceData.deliveryFee.base }} for ≤{{ priceData.deliveryFee.baseReports }} reports +
            €{{ priceData.deliveryFee.additionalPerReport }} for each extra report
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PriceTable",
  props: {
    priceData: {
      type: Object,
      required: true
    },
    viewSize: {
      type: String,
      default: 'small',
      validator: value => ['small', 'medium', 'large'].includes(value)
    }
  },
  methods: {
    getTableLayoutClass(category) {
      // For larger screens, display side-by-side tables for Diamond Reports
      if (this.viewSize !== 'small' && category.title === 'Diamond Reports') {
        return 'table-layout-dual';
      }
      return 'table-layout-single';
    },
    getTableClass(category, table) {
      const classes = ['table-wrapper'];

      // For dual layout tables
      if (this.viewSize !== 'small' && category.title === 'Diamond Reports') {
        if (table.name && table.name.includes('Laboratory-Grown')) {
          classes.push('table-full-width');
        } else {
          classes.push('table-half-width');
        }
      }

      return classes.join(' ');
    },
    getCellClass(cellIndex, totalCells) {
      const classes = [];
      if (cellIndex === 0) {
        classes.push('cell-first');
      }
      if (cellIndex === totalCells - 1) {
        classes.push('cell-last');
      }
      return classes.join(' ');
    }
  }
};
</script>

<style lang="scss" scoped>
.price-table-wrapper {
  width: 100%;
  margin: 20px auto;
}

// Header Styles
.price-list-header {
  text-align: center;
  padding: 25px 15px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 25px;

  .price-list-title {
    font-size: 24px;
    font-weight: bold;
    color: #2c3e50;
    margin: 0 0 12px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .price-list-currency {
    font-size: 16px;
    font-weight: 600;
    color: #e74c3c;
    margin: 0 0 12px 0;
  }

  .price-list-note {
    font-size: 13px;
    color: #7f8c8d;
    margin: 0;
    line-height: 1.6;
    font-style: italic;
  }
}

// Container Styles
.price-table-container {
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 20px;
  overflow-x: auto;
}

// Category Styles
.table-category {
  margin-bottom: 35px;

  &:last-child {
    margin-bottom: 0;
  }

  .category-title {
    font-size: 18px;
    font-weight: bold;
    color: #2c3e50;
    margin-bottom: 15px;
    padding: 10px;
    background-color: #f8f9fa;
    border-left: 4px solid #3498db;
    border-radius: 4px;
  }
}

// Table Layout
.table-layout-single {
  .table-wrapper {
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.table-layout-dual {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;

  .table-half-width {
    flex: 1 1 calc(50% - 10px);
    min-width: 280px;
  }

  .table-full-width {
    flex: 1 1 100%;
  }
}

// Table Styles
.price-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;

  thead {
    .table-name-row th {
      background-color: #2c3e50;
      color: #ffffff;
      font-size: 14px;
      font-weight: bold;
      padding: 12px 10px;
      text-align: center;
    }

    .table-header-row th {
      background-color: #ecf0f1;
      color: #2c3e50;
      font-size: 12px;
      font-weight: 600;
      padding: 10px 8px;
      border-bottom: 2px solid #3498db;
      text-align: left;

      &:last-child {
        text-align: right;
      }
    }
  }

  tbody {
    tr {
      &:nth-child(even) {
        background-color: #f9f9f9;
      }

      &:hover {
        background-color: #e8f4fd;
      }

      td {
        padding: 10px 8px;
        border-bottom: 1px solid #e0e0e0;
        color: #333;

        &.cell-first {
          font-weight: 500;
        }

        &.cell-last {
          text-align: right;
          font-weight: bold;
          color: #e74c3c;
        }
      }

      &.highlighted-row {
        background-color: #fff3cd !important;

        td {
          font-weight: bold;
        }
      }
    }
  }
}

// Delivery Note
.delivery-note {
  margin-top: 25px;
  padding: 18px;
  background-color: #f0f8ff;
  border: 1px solid #b8daff;
  border-radius: 4px;

  p {
    margin: 0;
    font-size: 14px;
    color: #004085;

    strong {
      color: #002752;
    }
  }
}

// Responsive adjustments for medium screens
@media (min-width: 768px) {
  .price-list-header {
    .price-list-title {
      font-size: 28px;
    }

    .price-list-currency {
      font-size: 18px;
    }

    .price-list-note {
      font-size: 14px;
    }
  }

  .category-title {
    font-size: 20px !important;
  }

  .price-table {
    font-size: 14px;

    thead {
      .table-name-row th {
        font-size: 15px;
      }

      .table-header-row th {
        font-size: 13px;
      }
    }
  }
}

// Responsive adjustments for large screens
@media (min-width: 1440px) {
  .price-table-wrapper {
    max-width: 1400px;
  }

  .price-list-header {
    .price-list-title {
      font-size: 32px;
    }

    .price-list-currency {
      font-size: 20px;
    }

    .price-list-note {
      font-size: 15px;
    }
  }

  .price-table-container {
    padding: 30px;
  }

  .category-title {
    font-size: 22px !important;
  }

  .price-table {
    font-size: 15px;

    thead {
      .table-name-row th {
        font-size: 16px;
        padding: 14px 12px;
      }

      .table-header-row th {
        font-size: 14px;
        padding: 12px 10px;
      }
    }

    tbody tr td {
      padding: 12px 10px;
    }
  }

  .delivery-note {
    padding: 20px;

    p {
      font-size: 15px;
    }
  }
}
</style>